import React, { useEffect, useState, useRef } from "react";
import useAdminClasses from "../../market/warehouse-baserow/useAdminClasses";
import { useUser } from "../../utensils/UserContext";
import SnackNewClass from "./SnackNewClass";
import SnackNewReservation from "./SnackNewReservation";
import "./SnackMyClass.css";
import { FaTrash, FaEdit, FaCalendarPlus } from "react-icons/fa";
import { useConfig } from "../../utensils/ConfigContext";
import SnackAttendanceModal from "./SnackAttendanceModal";
import useAdminReservation from "../../market/warehouse-baserow/useAdminReservation";
import { Tabs, Tab } from "react-bootstrap";

// === Helpers de horario (nuevo formato) ===
const minutesDiff = (inicio, fin) => {
  if (!inicio || !fin) return 0;
  const [sh, sm] = String(inicio).split(":").map(Number);
  const [eh, em] = String(fin).split(":").map(Number);
  const diff = eh * 60 + em - (sh * 60 + sm);
  return diff > 0 ? diff : 0;
};

const safeParse = (v, fallback = {}) => {
  try {
    return typeof v === "string" ? JSON.parse(v) : v || fallback;
  } catch {
    return fallback;
  }
};

// La tabla de reservas usa clave `${r.Clase}||${r.Reserva}`.
// Según cómo guardes "Reserva", intenta 2 variantes para compatibilidad.
const makeReservaKeyVariants = (classId, day, inicio, fin, mins) => {
  const base1 = `${day}: ${inicio} - ${fin} (${mins} min)`; // formato "viejo" con minutos
  const base2 = `${day}: ${inicio} - ${fin}`; // formato "simple" sin mins
  return [`${classId}||${base1}`, `${classId}||${base2}`];
};

const usadosPorFranja = (reservasPorHorario, classId, day, inicio, fin) => {
  const mins = minutesDiff(inicio, fin);
  const [k1, k2] = makeReservaKeyVariants(classId, day, inicio, fin, mins);
  return reservasPorHorario[k1] ?? reservasPorHorario[k2] ?? 0;
};

const SnackMyClass = ({ shouldRefresh, refreshKey }) => {
  const { classes, loading, error, refetch, deleteClass } = useAdminClasses();
  const { user } = useUser();
  const { apiToken } = useConfig();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [reservasPorHorario, setReservasPorHorario] = useState({});
  const { getReservationsByClassId, deleteReservation } = useAdminReservation();
  const [zonaActiva, setZonaActiva] = useState("ZONA VIP METAGYM");

  const intervalRef = useRef(null);

  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [selectedClassSchedule, setSelectedClassSchedule] = useState({
    classId: null,
    day: null,
  });

  const isUserRole = user?.Rol === "2-usuario";
  const isInClassTab = window.location.pathname.includes("reservas");

  const fetchReservas = async () => {
    try {
      const res = await fetch(
        `https://api.baserow.io/api/database/rows/table/569475/?user_field_names=true&size=200`,
        {
          headers: {
            Authorization: `Token ${apiToken}`,
          },
        }
      );
      const data = await res.json();
      const agrupadas = {};
      data.results.forEach((r) => {
        const clave = `${r.Clase}||${r.Reserva}`;
        if (!agrupadas[clave]) agrupadas[clave] = 0;
        agrupadas[clave]++;
      });
      setReservasPorHorario(agrupadas);
    } catch (err) {
      console.error("Error cargando reservas:", err.message);
    }
  };

  useEffect(() => {
    if (apiToken && isInClassTab) fetchReservas();
  }, [apiToken, isInClassTab]);

  useEffect(() => {
    if (!shouldRefresh || !apiToken) return;

    const updatePeriodically = () => {
      if (document.visibilityState === "visible") {
        fetchReservas();
      }
    };

    intervalRef.current = setInterval(updatePeriodically, 10000);

    document.addEventListener("visibilitychange", updatePeriodically);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", updatePeriodically);
    };
  }, [apiToken, shouldRefresh]);

  useEffect(() => {
    if (refreshKey && apiToken) {
      console.log("🔁 Refrescando clases por refreshKey...");
      refetch();
      fetchReservas();
    }
  }, [refreshKey, apiToken]);

  const openNewClass = () => {
    setEditingId(null);
    setModalOpen(true);
  };

  const openEditClass = (id) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const openReservation = (classId) => {
    setSelectedClassId(classId);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingId(null);
    setSelectedClassId(null);
    refetch();
    fetchReservas();
  };

  const handleDelete = async (classId) => {
    const confirm = window.confirm(
      "¿Estás seguro de eliminar esta clase y todas sus reservas?"
    );
    if (!confirm) return;

    try {
      const reservas = await getReservationsByClassId(classId);

      // 🔔 Extraer emails únicos
      const emails = [...new Set(reservas.map((r) => r.Email).filter(Boolean))];

      // 🔔 Obtener nombre de la clase (opcional)
      const claseNombre =
        classes.find((c) => c.id === classId)?.Nombre || "Clase eliminada";

      // 🔔 Notificar antes de eliminar
      if (emails.length > 0) {
        await fetch("https://fisiofitnessapp.com/mail/deleted_class.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clase: claseNombre, emails }),
        });
      }

      // 🗑️ Eliminar reservas
      for (const reserva of reservas) {
        try {
          await deleteReservation(reserva.id);
        } catch (err) {
          console.warn(
            `No se pudo eliminar reserva ID ${reserva.id}:`,
            err.message
          );
        }
      }

      // 🗑️ Eliminar clase
      await deleteClass(classId);

      refetch();
      fetchReservas();
    } catch (err) {
      console.error("Error eliminando clase o reservas:", err.message);
      alert("Ocurrió un error al intentar eliminar la clase o sus reservas.");
    }
  };

  const openAttendanceList = (classId, day) => {
    setSelectedClassSchedule({ classId, day });
    setAttendanceModalOpen(true);
  };

  // Reemplaza COMPLETAMENTE por esta versión (usa los helpers ya definidos arriba: safeParse, minutesDiff, usadosPorFranja)
  // Firma mantiene tu uso: formatSchedule(cls.Programacion, cls.Cupo, cls.id)
  const formatSchedule = (jsonString, cupo, classId) => {
    try {
      const obj = safeParse(jsonString, {});
      const orderedDays = [
        "Lunes",
        "Martes",
        "Miércoles",
        "Jueves",
        "Viernes",
        "Sábado",
        "Domingo",
      ];

      return orderedDays.map((day) => {
        const dayObj = obj?.[day] || { titulo: "", franjas: [] };
        const titulo = dayObj.titulo || "";
        const franjas = Array.isArray(dayObj.franjas) ? dayObj.franjas : [];
        const totalDia = franjas.reduce(
          (acc, f) => acc + minutesDiff(f.inicio, f.fin),
          0
        );

        return (
          <div key={`${classId}-${day}`} className="schedule-day">
            {/* Header: chips día + título + total */}
            <div className="day-header">
              <span className="chip chip-day">{day}</span>
              {titulo ? (
                <span className="chip chip-title">{titulo}</span>
              ) : null}
              {totalDia > 0 ? (
                <small className="ms-auto total-dia">
                  Total: {totalDia} min
                </small>
              ) : null}
            </div>

            {/* Franjas */}
            {franjas.length > 0 ? (
              <div className="franjas">
                {franjas.map((f, idx) => {
                  const mins = minutesDiff(f.inicio, f.fin);
                  const usados = usadosPorFranja(
                    reservasPorHorario, // 👈 variable del componente (estado)
                    classId,
                    day,
                    f.inicio,
                    f.fin
                  );
                  const disponibles = Math.max((Number(cupo) || 0) - usados, 0);

                  return (
                    <div key={`${classId}-${day}-${idx}`}>
                      <div className="franja-item">
                        <span className="franja-dot" />
                        <span className="franja-time">
                          {f.inicio} – {f.fin}
                        </span>
                        {mins ? (
                          <span className="franja-mins">({mins} min)</span>
                        ) : null}
                        <span className="ms-auto">
                          <strong className="text-success">
                            {disponibles}
                          </strong>{" "}
                          cupos
                        </span>
                      </div>
                      <div className="franja-actions">
                        <button
                          className="btn btn-att"
                          onClick={() => openAttendanceList(classId, day)}
                        >
                          Lista de asistencia
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="franjas">-</div>
            )}
          </div>
        );
      });
    } catch (error) {
      console.error("Error al parsear Programacion:", error);
      return <span className="text-danger">Horario no válido</span>;
    }
  };

  if (loading) return <p>Cargando clases...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="training-plan-container">
      <div className="d-flex justify-content-between align-items-center p-3 bg-white rounded shadow-sm mb-4 border">
        <h2 className="m-0">Clases programadas</h2>
        {!isUserRole && (
          <button className="btn btn-success" onClick={openNewClass}>
            Nueva clase
          </button>
        )}
      </div>
      <Tabs
        activeKey={zonaActiva}
        onSelect={(k) => setZonaActiva(k)}
        className="mb-3"
      >
        {["ZONA VIP METAGYM", "ZONA VIP FITNESS FOR LIFE"].map((zona) => (
          <Tab eventKey={zona} title={zona} key={zona}>
            <div className="workouts-cards-container">
              {classes
                .filter((cls) => cls.Zona === zona)
                .map((cls) => (
                  <div key={cls.id} className="card shadow-sm p-3 mb-3">
                    <h5 className="text-primary">
                      {cls.Nombre} - ({cls.Cupo} Cupos)
                    </h5>
                    <p>
                      <strong>Instructor:</strong> {cls.Instructor}
                    </p>
                    <p>
                      <strong>Detalle:</strong> {cls.Detalle}
                    </p>
                    <p>
                      <strong>Horario:</strong>
                      <div className="ms-2 schedule-scroll">
                        {formatSchedule(cls.Programacion, cls.Cupo, cls.id)}
                      </div>
                    </p>
                    <p>
                      <strong>Zona:</strong> {cls.Zona}
                    </p>

                    <div className="mt-3 d-flex justify-content-between">
                      {isUserRole ? (
                        <button
                          className="btn btn-outline-success btn-sm w-100"
                          onClick={() => openReservation(cls.id)}
                        >
                          <FaCalendarPlus className="me-1" /> Reservar
                        </button>
                      ) : (
                        <>
                          <button
                            className="btn btn-outline-primary btn-sm me-2"
                            onClick={() => openEditClass(cls.id)}
                          >
                            <FaEdit className="me-1" /> Editar
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(cls.id)}
                          >
                            <FaTrash className="me-1" /> Eliminar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </Tab>
        ))}
      </Tabs>

      {!isUserRole ? (
        <SnackNewClass
          show={modalOpen}
          onHide={handleModalClose}
          classId={editingId}
          onSave={handleModalClose}
        />
      ) : (
        <SnackNewReservation
          show={modalOpen}
          onHide={handleModalClose}
          classId={selectedClassId}
          onSave={handleModalClose}
        />
      )}

      <SnackAttendanceModal
        show={attendanceModalOpen}
        onHide={() => setAttendanceModalOpen(false)}
        classId={selectedClassSchedule.classId}
        day={selectedClassSchedule.day}
      />
    </div>
  );
};

export default SnackMyClass;
